<template>
  <b-card class="mb-2 chart-card">
    <template #header>
      <div class="chart-header">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="BarChart2Icon"
            size="16"
            class="mr-75 text-primary"
          />
          <h6 class="card-title mb-0">
            Orders By Date
          </h6>
        </div>
        <download-menu
          filename="orders_by_date"
          :rows="ordersOverTime"
          :columns="chartDownloadColumns"
        />
      </div>
    </template>

    <div
      v-if="ordersOverTime.length"
      class="chart-wrap"
    >
      <analytics-orders-chart-inner
        :chart-data="chartData"
        :options="chartOptions"
        :height="160"
      />
    </div>
    <div
      v-else
      class="empty-chart-state"
    >
      <feather-icon
        icon="BarChartIcon"
        size="32"
        class="mb-1 text-muted"
      />
      <p class="text-muted mb-0 small">
        No data for the selected period
      </p>
    </div>
  </b-card>
</template>

<script>
import { BCard } from 'bootstrap-vue'
import { Bar } from 'vue-chartjs'
import DownloadMenu from './DownloadMenu.vue'

const formatCount = value => Number(value).toLocaleString()

const AXIS_TICK_BASE = {
  beginAtZero: true,
  fontSize: 12,
  fontStyle: 'bold',
  precision: 0,
  maxTicksLimit: 7,
  callback: formatCount,
}

const makeChartScales = () => ({
  xAxes: [{
    gridLines: { display: false },
    ticks: {
      fontSize: 12, fontColor: '#94a3b8', maxRotation: 0, autoSkip: true, maxTicksLimit: 10,
    },
  }],
  yAxes: [
    {
      id: 'y-orders',
      position: 'left',
      ticks: { ...AXIS_TICK_BASE, fontColor: '#0369a1' },
      gridLines: { color: 'rgba(226, 232, 240, 0.8)', borderDash: [3, 3], zeroLineColor: '#e2e8f0' },
    },
    {
      id: 'y-products',
      position: 'right',
      ticks: { ...AXIS_TICK_BASE, fontColor: '#059669' },
      gridLines: { display: false },
    },
  ],
})

const AnalyticsOrdersChartInner = {
  name: 'AnalyticsOrdersChartInner',
  extends: Bar,
  props: {
    chartData: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
  },
  watch: {
    chartData(newVal) {
      // Destroy and fully recreate the chart so dual Y-axes are re-initialised
      // with the actual data range (chart.update() does not re-register axes)
      if (this.$data._chart) this.$data._chart.destroy()
      this.$nextTick(() => this.renderChart(newVal, this.options))
    },
  },
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
}

export default {
  name: 'AnalyticsOrdersChart',
  components: { BCard, AnalyticsOrdersChartInner, DownloadMenu },
  props: {
    ordersOverTime: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    chartDownloadColumns() {
      return [
        { key: 'date', label: 'Date' },
        { key: 'orders', label: 'Orders' },
        { key: 'products', label: 'Products (Units)' },
      ]
    },
    chartData() {
      return {
        labels: this.ordersOverTime.map(d => d.date),
        datasets: [
          {
            label: 'Orders',
            yAxisID: 'y-orders',
            backgroundColor: 'rgba(3, 105, 161, 0.65)',
            hoverBackgroundColor: 'rgba(3, 105, 161, 0.85)',
            data: this.ordersOverTime.map(d => d.orders),
          },
          {
            label: 'Products',
            yAxisID: 'y-products',
            backgroundColor: 'rgba(5, 150, 105, 0.6)',
            hoverBackgroundColor: 'rgba(5, 150, 105, 0.85)',
            data: this.ordersOverTime.map(d => d.products || 0),
          },
        ],
      }
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 8 } },
        legend: {
          display: true,
          position: 'top',
          labels: {
 usePointStyle: true, fontSize: 11, fontColor: '#64748b', padding: 16,
},
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#0f172a',
          titleFontSize: 11,
          bodyFontSize: 11,
          bodySpacing: 4,
          xPadding: 10,
          yPadding: 8,
          callbacks: {
            label(tooltipItem, data) {
              const ds = data.datasets[tooltipItem.datasetIndex]
              return ` ${ds.label}: ${formatCount(tooltipItem.yLabel)}`
            },
          },
        },
        scales: makeChartScales(),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-card {
  border: 1px solid #e9eef6;
  border-radius: 10px;

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* Segmented metric toggle — pill style */
  .metric-tabs {
    display: flex;
    background: #f0f5ff;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
  }

  .metric-tab {
    min-height: 32px;         // touch-comfortable
    min-width: 56px;
    padding: 5px 12px;
    border-radius: 6px;
    border: none;
    background: transparent;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;           // 4.6:1 on #f0f5ff — AA
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
    line-height: 1;

    &:hover:not(&--active) {
      color: #1e3a8a;
      background: rgba(255, 255, 255, 0.6);
    }

    &:focus-visible {
      outline: 2px solid #1e40af;
      outline-offset: 1px;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &--active {
      background: #fff;
      color: #1e40af;         // 5.1:1 on #fff — AA
      font-weight: 600;
      box-shadow: 0 1px 4px rgba(30, 64, 175, 0.12);
    }
  }

  .chart-wrap {
    position: relative;
    height: 260px;
  }

  .empty-chart-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 260px;
    color: #94a3b8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .metric-tab {
    transition: none;
  }
}
</style>
